import { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  Network,
  TrendingUp,
  Activity,
  Layers,
  AlertCircle
} from 'lucide-react';
import type { DatabaseState, Study, Mushroom, Condition } from '@/types/database';

interface NetworkAnalyticsPanelProps {
  database: DatabaseState;
  isVisible: boolean;
}

interface NetworkMetrics {
  totalNodes: number;
  totalEdges: number;
  mostConnectedSpecies: Array<{ name: string; connections: number }>;
  mostStudiedCompounds: Array<{ name: string; studyCount: number }>;
  mostResearchedConditions: Array<{ name: string; studyCount: number }>;
  isolatedStudies: number;
  studyTypeDistribution: Array<{ name: string; value: number }>;
  researchByYear: Array<{ year: number; count: number }>;
  speciesConditionMatrix: Array<{ species: string; condition: string; count: number }>;
}

const COLORS = ['#A98A2C', '#B98B36', '#C26A1E', '#B45F3D', '#6E5743', '#C8A53D'];

export function NetworkAnalyticsPanel({ database, isVisible }: NetworkAnalyticsPanelProps) {
  const metrics = useMemo<NetworkMetrics>(() => {
    if (!database) {
      return {
        totalNodes: 0,
        totalEdges: 0,
        mostConnectedSpecies: [],
        mostStudiedCompounds: [],
        mostResearchedConditions: [],
        isolatedStudies: 0,
        studyTypeDistribution: [],
        researchByYear: [],
        speciesConditionMatrix: []
      };
    }

    // Total nodes and edges
    const totalNodes =
      (database.studies?.length || 0) +
      (database.compounds?.length || 0) +
      (database.mushrooms?.length || 0) +
      (database.conditions?.length || 0);

    const totalEdges =
      (database.study_compounds?.length || 0) +
      (database.interventions?.length || 0) +
      (database.study_conditions?.length || 0);

    // Most connected species (by number of studies)
    const speciesConnectionMap = new Map<string, number>();
    database.interventions?.forEach(intervention => {
      const count = speciesConnectionMap.get(intervention.mushroom_id) || 0;
      speciesConnectionMap.set(intervention.mushroom_id, count + 1);
    });

    const mostConnectedSpecies = Array.from(speciesConnectionMap.entries())
      .map(([mushroomId, count]) => {
        const mushroom = database.mushrooms?.find(m => m.mushroom_id === mushroomId);
        return {
          name: mushroom?.common_name || 'Unknown',
          connections: count
        };
      })
      .sort((a, b) => b.connections - a.connections)
      .slice(0, 10);

    // Most studied compounds
    const compoundStudyMap = new Map<string, number>();
    database.study_compounds?.forEach(sc => {
      const count = compoundStudyMap.get(sc.compound_id) || 0;
      compoundStudyMap.set(sc.compound_id, count + 1);
    });

    const mostStudiedCompounds = Array.from(compoundStudyMap.entries())
      .map(([compoundId, count]) => {
        const compound = database.compounds?.find(c => c.compound_id === compoundId);
        return {
          name: compound?.name.substring(0, 30) || 'Unknown',
          studyCount: count
        };
      })
      .sort((a, b) => b.studyCount - a.studyCount)
      .slice(0, 10);

    // Most researched conditions
    const conditionStudyMap = new Map<string, number>();
    database.study_conditions?.forEach(sc => {
      const count = conditionStudyMap.get(sc.condition_id) || 0;
      conditionStudyMap.set(sc.condition_id, count + 1);
    });

    const mostResearchedConditions = Array.from(conditionStudyMap.entries())
      .map(([conditionId, count]) => {
        const condition = database.conditions?.find(c => c.condition_id === conditionId);
        return {
          name: condition?.name || 'Unknown',
          studyCount: count
        };
      })
      .sort((a, b) => b.studyCount - a.studyCount)
      .slice(0, 10);

    // Isolated studies (no compounds or interventions)
    const studiesWithCompounds = new Set(database.study_compounds?.map(sc => sc.study_id) || []);
    const studiesWithInterventions = new Set(database.interventions?.map(i => i.study_id) || []);
    const isolatedStudies = database.studies?.filter(study =>
      !studiesWithCompounds.has(study.study_id) && !studiesWithInterventions.has(study.study_id)
    ).length || 0;

    // Study type distribution
    const studyTypeMap = new Map<string, number>();
    database.studies?.forEach(study => {
      const type = study.study_type || 'Unknown';
      studyTypeMap.set(type, (studyTypeMap.get(type) || 0) + 1);
    });

    const studyTypeDistribution = Array.from(studyTypeMap.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    // Research by year
    const yearMap = new Map<number, number>();
    database.studies?.forEach(study => {
      if (study.year) {
        yearMap.set(study.year, (yearMap.get(study.year) || 0) + 1);
      }
    });

    const researchByYear = Array.from(yearMap.entries())
      .map(([year, count]) => ({ year, count }))
      .sort((a, b) => a.year - b.year)
      .slice(-20); // Last 20 years

    // Species × Condition matrix (top combinations)
    const matrixMap = new Map<string, number>();
    database.study_conditions?.forEach(sc => {
      const intervention = database.interventions?.find(i => i.study_id === sc.study_id);
      if (intervention) {
        const key = `${intervention.mushroom_id}:${sc.condition_id}`;
        matrixMap.set(key, (matrixMap.get(key) || 0) + 1);
      }
    });

    const speciesConditionMatrix = Array.from(matrixMap.entries())
      .map(([key, count]) => {
        const [mushroomId, conditionId] = key.split(':');
        const mushroom = database.mushrooms?.find(m => m.mushroom_id === mushroomId);
        const condition = database.conditions?.find(c => c.condition_id === conditionId);
        return {
          species: mushroom?.common_name.substring(0, 15) || 'Unknown',
          condition: condition?.name.substring(0, 20) || 'Unknown',
          count
        };
      })
      .filter(item => item.count > 1) // Only show combinations with 2+ studies
      .sort((a, b) => b.count - a.count)
      .slice(0, 50);

    return {
      totalNodes,
      totalEdges,
      mostConnectedSpecies,
      mostStudiedCompounds,
      mostResearchedConditions,
      isolatedStudies,
      studyTypeDistribution,
      researchByYear,
      speciesConditionMatrix
    };
  }, [database]);

  if (!isVisible) return null;

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-[#A98A2C]" />
          <CardTitle>Network Analytics</CardTitle>
        </div>
        <CardDescription>
          Comprehensive analysis of research network patterns and trends
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="metrics" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="metrics">Metrics</TabsTrigger>
            <TabsTrigger value="charts">Charts</TabsTrigger>
            <TabsTrigger value="matrix">Matrix</TabsTrigger>
          </TabsList>

          {/* Network Metrics Tab */}
          <TabsContent value="metrics" className="space-y-4">
            <ScrollArea className="h-[600px] pr-4">
              {/* Overview Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Network className="h-4 w-4" />
                      Total Nodes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-[#A98A2C]">{metrics.totalNodes}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Across all entity types
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Layers className="h-4 w-4" />
                      Total Connections
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-[#B98B36]">{metrics.totalEdges}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Relationships mapped
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      Isolated Studies
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-[#C26A1E]">{metrics.isolatedStudies}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Missing compound/species data
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Network Density
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-[#B45F3D]">
                      {((metrics.totalEdges / metrics.totalNodes) || 0).toFixed(2)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Avg connections per node
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Most Connected Species */}
              <Card className="mb-4">
                <CardHeader>
                  <CardTitle className="text-base">Most Connected Species</CardTitle>
                  <CardDescription>Hub analysis: Species with most clinical trials</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {metrics.mostConnectedSpecies.map((species, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{idx + 1}</Badge>
                          <span className="text-sm font-medium">{species.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-32 h-2 bg-[#E4E1D8] rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#C26A1E]"
                              style={{
                                width: `${(species.connections / metrics.mostConnectedSpecies[0].connections) * 100}%`
                              }}
                            />
                          </div>
                          <span className="text-sm text-muted-foreground w-12 text-right">
                            {species.connections}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Most Studied Compounds */}
              <Card className="mb-4">
                <CardHeader>
                  <CardTitle className="text-base">Most Studied Compounds</CardTitle>
                  <CardDescription>Compounds appearing in multiple trials</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {metrics.mostStudiedCompounds.map((compound, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{idx + 1}</Badge>
                          <span className="text-sm font-medium truncate">{compound.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-[#E4E1D8] rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#B98B36]"
                              style={{
                                width: `${(compound.studyCount / metrics.mostStudiedCompounds[0].studyCount) * 100}%`
                              }}
                            />
                          </div>
                          <span className="text-sm text-muted-foreground w-8 text-right">
                            {compound.studyCount}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Most Researched Conditions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Most Researched Conditions</CardTitle>
                  <CardDescription>Health conditions with most clinical evidence</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {metrics.mostResearchedConditions.map((condition, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{idx + 1}</Badge>
                          <span className="text-sm font-medium">{condition.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-[#E4E1D8] rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#B45F3D]"
                              style={{
                                width: `${(condition.studyCount / metrics.mostResearchedConditions[0].studyCount) * 100}%`
                              }}
                            />
                          </div>
                          <span className="text-sm text-muted-foreground w-8 text-right">
                            {condition.studyCount}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </ScrollArea>
          </TabsContent>

          {/* Charts Tab */}
          <TabsContent value="charts" className="space-y-4">
            <ScrollArea className="h-[600px] pr-4">
              {/* Top Species Bar Chart */}
              <Card className="mb-4">
                <CardHeader>
                  <CardTitle className="text-base">Top 10 Species by Trial Count</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={metrics.mostConnectedSpecies}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        height={100}
                        fontSize={11}
                      />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="connections" fill="#C8A53D" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Study Type Pie Chart */}
              <Card className="mb-4">
                <CardHeader>
                  <CardTitle className="text-base">Study Type Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={metrics.studyTypeDistribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#A98A2C"
                        dataKey="value"
                      >
                        {metrics.studyTypeDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {metrics.studyTypeDistribution.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                        />
                        <span className="text-xs">
                          {item.name}: {item.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Research Timeline */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Research Activity by Year</CardTitle>
                  <CardDescription>Publication trends over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={metrics.researchByYear}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="year"
                        type="number"
                        domain={['dataMin', 'dataMax']}
                      />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#C8A53D"
                        strokeWidth={2}
                        dot={{ fill: '#C8A53D', r: 4 }}
                        name="Publications"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </ScrollArea>
          </TabsContent>

          {/* Matrix Tab */}
          <TabsContent value="matrix" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Species × Condition Matrix</CardTitle>
                <CardDescription>
                  Top research combinations (showing pairs with 2+ studies)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[600px]">
                  <div className="space-y-2">
                    {metrics.speciesConditionMatrix.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="secondary">{item.species}</Badge>
                            <span className="text-xs text-muted-foreground">×</span>
                            <Badge variant="outline">{item.condition}</Badge>
                          </div>
                          <div className="w-full h-2 bg-[#E4E1D8] rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-[#C26A1E] to-[#C8A53D]"
                              style={{
                                width: `${(item.count / metrics.speciesConditionMatrix[0].count) * 100}%`
                              }}
                            />
                          </div>
                        </div>
                        <div className="ml-4">
                          <Badge variant="default">{item.count} studies</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
